import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, Plus } from 'lucide-react';
import type { ChatMessage } from '@draughts/shared';
import { CHAT_CONFIG, eMessaggioMio } from '@draughts/shared';
import { useChatStore } from '../../store/chat-store';
import { useAuthStore } from '../../store/auth-store';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useExpiryCountdown } from '../../hooks/useExpiryCountdown';
import { trackChatMessageSent } from '../../utils/analytics';
import ChatPresetPalette from './ChatPresetPalette';
import ChatReportModal from './ChatReportModal';
import './ChatPanel.css';
import { oraBreve } from '../../utils/formato-data';

function ChatPanel() {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  // Il cassetto delle frasi rapide: chiuso di default, così per tutta la partita
  // la lista dei messaggi resta a tutta altezza. Gli ospiti non lo usano — la
  // loro tavolozza è sempre a vista.
  const [presetsOpen, setPresetsOpen] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Il cassetto e la riga della casella di testo: quel che sta dentro non conta
  // come "fuori" per la chiusura al tocco.
  const composerRef = useRef<HTMLDivElement>(null);
  // Il bottone che apre il cassetto: dopo un invio il chip toccato sparisce e
  // il fuoco finirebbe su <body>, che da tastiera è un vicolo cieco.
  const presetToggleRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  // Subscribe to individual store slices so ChatPanel only re-renders when
  // a field it actually reads changes. Previously the full-store destructure
  // re-rendered on every mutation (opponent moves, clock ticks, presence) —
  // contributing to drag jank in online games. See diagnosis notes.
  //
  // Everything comes from chat-store, which holds the conversation AND whose
  // game it belongs to. That is what makes this panel work identically in an
  // online game, an arena game and a tournament game.
  const chatMessages = useChatStore((s) => s.messages);
  const chatOpen = useChatStore((s) => s.isOpen);
  const unreadCount = useChatStore((s) => s.unreadCount);
  const playerColor = useChatStore((s) => s.playerColor);
  /* Con l'avversario uscito a fine partita il campo si spegne: il messaggio
     non lo leggerebbe nessuno. */
  const opponentLeft = useChatStore((s) => s.opponentLeft);
  const limiteRaggiunto = useChatStore((s) => s.limiteRaggiunto);
  /* La chat e' chiusa: la stanza che la teneva viva non c'e' piu'. La chiusura
     passa da `canChat`, quindi campo, cassetto e bottone d'invio si spengono da
     se'; qui serve a DIRLO, che era la meta' che mancava. */
  const chatChiusa = useChatStore((s) => s.chatChiusa);
  const chatClosesAt = useChatStore((s) => s.chatClosesAt);
  const scadeChiusuraChat = useChatStore((s) => s.scadeChiusuraChat);
  const canChat = useChatStore((s) => s.canChat) && !opponentLeft;
  const sendChat = useChatStore((s) => s.send);
  const toggleChat = useChatStore((s) => s.toggle);
  const roomId = useChatStore((s) => s.roomId);
  const opponentReported = useChatStore((s) => s.opponentReported);
  const markOpponentReported = useChatStore((s) => s.markOpponentReported);

  /*
   * L'ultimo minuto, contato. Il server manda `closesAt` solo dentro quel minuto,
   * quindi l'intervallo a 1 Hz vive sessanta secondi e non i cinque minuti della
   * schermata di fine partita. `onExpire` spegne l'avviso se la chiusura non
   * arriva: vuol dire che la finestra si e' allungata (rivincita, manche di
   * ritorno), e un conto fermo a zero sarebbe una bugia.
   */
  const secondiAllaChiusura = useExpiryCountdown(chatClosesAt, scadeChiusuraChat);

  // Guests get a restricted composer (curated emoji + preset phrases).
  // Registered users keep the full free-text input. The server enforces the
  // same policy authoritatively.
  const isGuest = useAuthStore((s) => s.isGuest);

  // Scroll to bottom when new messages arrive. We set scrollTop directly on the
  // messages container rather than calling scrollIntoView() on an anchor: with
  // nested scroll containers (the sidebar tabs wrap the chat) scrollIntoView
  // walks up and scrolls the wrong ancestor, leaving the latest message clipped
  // out of view — the bug players hit during games on mobile.
  // `presetsOpen` è fra le dipendenze di proposito: il cassetto accorcia la
  // lista, e senza rimetterla in fondo l'ultimo messaggio finirebbe fuori vista
  // proprio mentre stai per rispondere.
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (chatOpen && el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [chatMessages, chatOpen, presetsOpen]);

  // Focus input when chat opens — desktop only. On mobile, auto-focusing a
  // text input forces the on-screen keyboard open, shrinking the viewport and
  // pushing the just-received message out of view.
  useEffect(() => {
    if (!isMobile && chatOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [chatOpen, isMobile]);

  // Il cassetto non deve sopravvivere alla chiusura della chat né a una partita
  // che smette di accettare messaggi: riaprendo la chat si riparte da chiuso.
  useEffect(() => {
    if (!chatOpen || !canChat) setPresetsOpen(false);
  }, [chatOpen, canChat]);

  /*
   * Esc chiude il cassetto da OVUNQUE stia il fuoco, non solo da dentro la riga
   * della casella di testo: in fase di cattura su `document`, così arriva prima
   * del listener su window di FullscreenPanel, che altrimenti chiuderebbe il
   * pannello a tutto schermo lasciando il cassetto aperto sotto.
   */
  useEffect(() => {
    if (!presetsOpen) return;

    const onKeyDownCapture = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      setPresetsOpen(false);
      presetToggleRef.current?.focus();
    };

    document.addEventListener('keydown', onKeyDownCapture, true);
    return () => document.removeEventListener('keydown', onKeyDownCapture, true);
  }, [presetsOpen]);

  /*
   * Un tocco altrove lo richiude: sulla scacchiera, sui messaggi, ovunque.
   * `pointerdown` e non `click` perché su mobile il primo tocco che riporta la
   * mano sulla partita deve chiudere subito, senza aspettare il rilascio.
   * Il listener esiste solo mentre il cassetto è aperto.
   */
  useEffect(() => {
    if (!presetsOpen) return;

    const onPointerDown = (e: PointerEvent) => {
      const composer = composerRef.current;
      if (composer && !composer.contains(e.target as Node)) {
        setPresetsOpen(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [presetsOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    // Se il limite a raffica lo trattiene, il testo resta nel campo: l'avviso
    // sotto dice di aspettare, e basta premere di nuovo.
    if (!sendChat(inputValue)) return;
    trackChatMessageSent();
    setInputValue('');
  };

  const handleGuestSend = (wireMessage: string) => {
    if (!sendChat(wireMessage)) return;
    trackChatMessageSent();
  };

  /** Un tocco nel cassetto manda e richiude: è un gesto solo, non una sessione. */
  const handlePresetSend = (wireMessage: string) => {
    if (!sendChat(wireMessage)) return;
    trackChatMessageSent();
    setPresetsOpen(false);
    // Il chip appena toccato sta per sparire: il fuoco torna al bottone che lo
    // ha aperto, non a <body>.
    presetToggleRef.current?.focus();
  };

  /*
   * Esc a cassetto chiuso: chiude la chat, come ha sempre fatto. Col cassetto
   * aperto qui non ci arriva mai — se lo mangia prima il listener in cattura
   * qui sopra, che è l'unico a decidere la precedenza. Sta sulla form e non
   * sull'input così vale per tutta la riga, senza scattare due volte risalendo.
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      toggleChat();
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return oraBreve(date);
  };

  // The opponent's last message: whatever made the player reach for the button,
  // and the context the report carries. Reporting needs one — with nothing said
  // there is nothing to moderate, and a button that always fails is worse than
  // no button.
  const opponentMessages = chatMessages.filter((m) => !m.isSystem && !eMessaggioMio(m, playerColor));
  const lastOpponentMessage = opponentMessages.length > 0 ? opponentMessages[opponentMessages.length - 1]! : null;
  /* `!chatChiusa`: col trascritto autorevole andato via con la stanza il server
     rifiuta la segnalazione (`TRANSCRIPT_GONE`), e un bottone che fallisce sempre
     e' peggio di nessun bottone. */
  const canReport = Boolean(roomId && playerColor && lastOpponentMessage) && !chatChiusa;

  // A reported opponent is muted for the rest of the session.
  const visibleMessages = opponentReported
    ? chatMessages.filter((m) => eMessaggioMio(m, playerColor))
    : chatMessages;

  return (
    <div className={`chat-panel ${chatOpen ? 'open' : 'closed'}`}>
      <div className="chat-header" onClick={toggleChat}>
        <span className="chat-title">{t('chat.title')}</span>
        {!chatOpen && unreadCount > 0 && <span className="chat-badge">{unreadCount}</span>}
        {/*
         * Il bottone DICE cosa fa. Il suo contenuto è un glifo — ✕ o ▼ — che uno
         * screen reader legge «per» o «triangolo nero rivolto verso il basso»:
         * il bottone funzionava da tastiera, semplicemente non si annunciava.
         * `aria-expanded` aggiunge lo stato, che è l'altra metà dell'informazione.
         */}
        <button
          className="chat-toggle"
          type="button"
          aria-label={chatOpen ? t('chat.closeChat') : t('chat.openChat')}
          aria-expanded={chatOpen}
        >
          <span aria-hidden="true">{chatOpen ? '\u2715' : '\u25BC'}</span>
        </button>
      </div>

      {chatOpen && (
        <>
          <div className="chat-messages" ref={messagesContainerRef}>
            {visibleMessages.length === 0 ? (
              <div className="chat-empty">{t('chat.empty')}</div>
            ) : (
              visibleMessages.map((msg) => (
                <ChatMessageItem
                  key={msg.id}
                  message={msg}
                  isOwn={eMessaggioMio(msg, playerColor)}
                  formatTime={formatTime}
                />
              ))
            )}
          </div>

          {/* Report row, not a header button: the chat header is display:none
              inside the mobile FullscreenPanel, and mobile is where this matters
              most. Sits in the panel body so both mounts show it. */}
          {opponentLeft && (
            <div className="chat-actions muted" role="status">
              {t('chat.opponentLeft')}
            </div>
          )}

          {chatChiusa && (
            <div className="chat-actions muted" role="status" data-testid="chat-chiusa">
              {t('chat.closed')}
            </div>
          )}

          {!chatChiusa && secondiAllaChiusura > 0 && (
            <div className="chat-actions muted chat-avviso" role="status" data-testid="chat-chiusura-vicina">
              {t('chat.closingIn', { seconds: secondiAllaChiusura })}
            </div>
          )}

          {limiteRaggiunto && (
            <div className="chat-actions muted chat-avviso" role="status" data-testid="chat-limite">
              {t('chat.rateLimited')}
            </div>
          )}

          {opponentReported ? (
            <div className="chat-actions muted" role="status">
              {t('chat.report.muted')}
            </div>
          ) : (
            canReport && (
              <div className="chat-actions">
                <button
                  type="button"
                  className="chat-report-trigger"
                  onClick={() => setReportOpen(true)}
                >
                  <Flag size={15} aria-hidden="true" />
                  {t('chat.report.trigger')}
                </button>
              </div>
            )
          )}

          {isGuest ? (
            <>
              <div className="chat-guest-hint">{t('chat.guestHint')}</div>
              <ChatPresetPalette onSend={handleGuestSend} disabled={!canChat} />
            </>
          ) : (
            <div className="chat-composer" ref={composerRef}>
              {presetsOpen && (
                <ChatPresetPalette
                  id="chat-preset-drawer"
                  onSend={handlePresetSend}
                  disabled={!canChat}
                />
              )}

              <form className="chat-input-form" onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
                <input
                  ref={inputRef}
                  type="text"
                  className="chat-input"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onFocus={() => setPresetsOpen(false)}
                  placeholder={canChat ? t('chat.placeholder') : t('chat.unavailable')}
                  disabled={!canChat}
                  maxLength={CHAT_CONFIG.MAX_MESSAGE_LENGTH}
                />
                <button
                  ref={presetToggleRef}
                  type="button"
                  className="chat-preset-toggle"
                  onClick={() => setPresetsOpen((aperto) => !aperto)}
                  disabled={!canChat}
                  aria-label={t('chat.quickPhrases')}
                  aria-expanded={presetsOpen}
                  aria-controls="chat-preset-drawer"
                >
                  {/* Un segno + come su Messenger: «altre cose che puoi mandare».
                      22 e non 20 come le altre icone perché un glifo senza sagoma
                      che lo contenga pesa meno, e a 20 resterebbe smilzo nei 44px. */}
                  <Plus size={22} aria-hidden="true" />
                </button>
                <button
                  type="submit"
                  className="chat-send-button"
                  disabled={!canChat || !inputValue.trim()}
                >
                  {t('common.send')}
                </button>
              </form>
            </div>
          )}
        </>
      )}

      {/* Outside the chatOpen block on purpose: collapsing the chat behind the
          dialog must not unmount the dialog. */}
      {reportOpen && canReport && (
        <ChatReportModal
          roomId={roomId!}
          opponentUsername={lastOpponentMessage!.senderUsername}
          reportedMessage={lastOpponentMessage}
          onClose={() => setReportOpen(false)}
          onReported={markOpponentReported}
        />
      )}
    </div>
  );
}

interface ChatMessageItemProps {
  message: ChatMessage;
  isOwn: boolean;
  formatTime: (timestamp: number) => string;
}

function ChatMessageItem({ message, isOwn, formatTime }: ChatMessageItemProps) {
  const { t } = useTranslation();
  // Le frasi pronte viaggiano come chiave stabile, così ogni giocatore le legge in
  // their own language; fall back to the raw text for free-text messages.
  const content = message.presetKey ? t(`chat.presets.${message.presetKey}`) : message.message;
  return (
    <div className={`chat-message ${isOwn ? 'own' : 'opponent'} ${message.senderColor}`}>
      <div className="chat-message-header">
        <span className="chat-sender">{message.senderUsername}</span>
        <span className="chat-time">{formatTime(message.timestamp)}</span>
      </div>
      <div className="chat-message-content">{content}</div>
    </div>
  );
}

export default ChatPanel;
